import { useToast } from '@chakra-ui/react';
import { ApiClientExceptionErrorCode, IApiClientException } from 'types/api';
import { ApiClientException } from 'utils/apiClientException';

type HTTPInit = Omit<RequestInit, 'method' | 'body'>;

export abstract class HTTP {
  static getDefaultHeaders() {
    return new Headers({});
  }

  private static getInput(endpoint: string) {
    const baseUrl =
      (typeof mondayMindbody !== 'undefined' && mondayMindbody?.apiBaseUrl) ||
      import.meta.env.VITE_API_BASE_URL ||
      `${window.location.origin}/wp-json/mindbody/v1`;

    if (endpoint.charAt(0) !== '/') endpoint = '/' + endpoint;

    return baseUrl + endpoint;
  }

  public static get<T>(endpoint: string, params = {}, init?: HTTPInit) {
    const queryparams = new URLSearchParams(params).toString();
    if (queryparams) endpoint += `?${queryparams}`;

    return HTTP.request<T>(HTTP.getInput(endpoint), { method: 'GET', ...init });
  }

  public static post<T>(endpoint: string, body = {}, init?: HTTPInit) {
    return HTTP.request<T>(HTTP.getInput(endpoint), {
      method: 'POST',
      ...init,
      body: JSON.stringify(body),
    });
  }

  public static patch<T>(endpoint: string, body = {}, init?: HTTPInit) {
    return HTTP.request<T>(HTTP.getInput(endpoint), {
      method: 'PATCH',
      ...init,
      body: JSON.stringify(body),
    });
  }

  public static put<T>(endpoint: string, body = {}, init?: HTTPInit) {
    return HTTP.request<T>(HTTP.getInput(endpoint), {
      method: 'PUT',
      ...init,
      body: JSON.stringify(body),
    });
  }

  public static delete<T>(endpoint: string) {
    return HTTP.request<T>(HTTP.getInput(endpoint), { method: 'DELETE' });
  }

  private static request<T>(input: RequestInfo, init: RequestInit) {
    return new HTTPRequest<T>(input, init, HTTP.getDefaultHeaders());
  }
}

class HTTPRequest<T> {
  constructor(
    private readonly input: RequestInfo,
    private readonly init: RequestInit,
    private readonly headers = new Headers({}),
  ) {}

  private abortController = new AbortController();

  async send(): Promise<T> {
    return fetch(this.input, {
      ...this.init,
      credentials: 'include',
      headers: this.headers,
      signal: this.abortController.signal,
    }).then((response) => {
      // Verdict du miroir membre posé par le serveur (header absent = flag off
      // ou route non miroirée). Affiché en console pour diagnostiquer les tests :
      // hit = servi local, miss/stale/expired = fetch live + stockage, grace = API down.
      const mirrorVerdict = response.headers.get('X-Monday-Mirror');
      if (mirrorVerdict) {
        const url = typeof this.input === 'string' ? this.input : this.input.url;
        console.info(`[member-mirror] ${url.replace(/^.*\/wp-json\/mindbody\/v1/, '')} -> ${mirrorVerdict}`);
      }

      if (response.ok) return response.json() as Promise<T>;
      return response.json().then((data: IApiClientException & { code?: string }) => {
        // Refus du garde-fou des sites de test (mu-plugin mindbody-local-guard, local et stagings, jamais en prod) :
        // erreur au format WordPress ({ code, message }), sans Message. Code repris pour que l'écran dise « site de
        // test » (errors.mm_local_guard) au lieu d'un échec générique (retour de Ségolène du 15/10 : « Vos
        // informations n'ont pas pu être enregistrées » sur le staging RIISE, écriture bloquée par le garde-fou).
        if (!data?.Message && data?.code === ApiClientExceptionErrorCode.TestSiteGuard) {
          throw new ApiClientException(
            ApiClientExceptionErrorCode.TestSiteGuard,
            response.status,
            ApiClientExceptionErrorCode.TestSiteGuard,
          );
        }
        throw new ApiClientException(data.Message, data.Code, data.ErrorCode);
      });
    });
  }

  abort() {
    this.abortController.abort();
  }

  setHeader(name: string, value: string) {
    this.headers.set(name, value);
    return this;
  }

  unsetHeader(name: string) {
    this.headers.delete(name);
    return this;
  }
}

export function useCopyToClipboard() {
  const toast = useToast();

  const copyToClipboard = (element?: HTMLInputElement, successMessage?: string) => {
    if (element) element.select();

    if (!navigator.clipboard) {
      document.execCommand('copy');
    } else {
      const text = window.getSelection()?.toString();
      if (text) {
        navigator.clipboard.writeText(text);
      }
    }

    document.getSelection()?.removeAllRanges();

    toast({ title: successMessage, status: 'success' });
  };

  return copyToClipboard;
}
